import { Component, Input, ChangeDetectionStrategy, ChangeDetectorRef, OnInit } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { CreateWorkRestrictionRuleModalComponent } from './modals/create-work-restriction-rule-modal/create-work-restriction-rule-modal.component';
import { RuleErrorModalComponent, RuleError } from './modals/rule-error-modal/rule-error-modal.component';
import { WorkRestrictionRulesService } from './work-restrictions-rules.service';
import { WorkRestrictionRule as ApiRule, IdName, WorkRestrictionRuleDisplay, RulesGroup } from './work-restrictions-rules.interfaces';
import { forkJoin, of } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';

@Component({
  selector: 'app-work-restrictions-rules',
  templateUrl: './work-restrictions-rules.component.html',
  styleUrls: ['./work-restrictions-rules.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class WorkRestrictionsRulesComponent implements OnInit {
  @Input() filter: any;
  @Input() calendar: any;

  districts: IdName[] = [];
  workTypes: IdName[] = [];
  ruleTypes: IdName[] = [
    { id: 'schedule', name: 'По расписанию' },
    { id: 'day', name: 'День-в-день' },
  ];
  statuses: IdName[] = [
    { id: 'active', name: 'Активно' },
    { id: 'inactive', name: 'Неактивно' },
  ];

  selectedDistrict: IdName | null = null;
  selectedWorkType: IdName | null = null;
  selectedRuleType: IdName | null = null;
  selectedStatus: IdName | null = null;

  groups: RulesGroup[] = [];
  isLoading = false;
  isSaving = false;
  error: string | null = null;
  successMessage: string | null = null;

  private readonly weekDayNames = ['', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
  // Словарь district_id -> district_name
  private districtNamesMap: { [key: number]: string } = {};

  // Храним оригинальные (полные) правила для фильтрации на клиенте
  private originalRules: ApiRule[] = [];

  constructor(
    private modalService: NgbModal,
    private cdr: ChangeDetectorRef,
    private rulesService: WorkRestrictionRulesService
  ) {}

  ngOnInit(): void {
    this.loadRules();
  }

  loadRules(): void {
    if (!this.filter || !this.filter.plotSelected || !this.filter.plotSelected.sector_id) {
      return;
    }

    this.isLoading = true;
    this.error = null;

    const sectorId = this.filter.plotSelected.sector_id;

    // Параллельно загружаем районы и типы работ
    forkJoin({
      districts: this.rulesService.getSectorDistricts(sectorId),
      workTypes: this.rulesService.getWorkTypes(),
    }).subscribe({
      next: ({ districts: districtsResponse, workTypes: workTypesResponse }) => {
        // Сохраняем типы работ
        this.workTypes = workTypesResponse.data.work_types.map((wt) => ({
          id: wt.id,
          name: wt.description,
        }));

        // Сохраняем районы
        const districts = districtsResponse.data;
        this.buildDistrictNamesMap(districts);
        this.districts = [];
        const districtIds: number[] = [];
        if (districts) {
          districts.forEach((d) => {
            if (d.d_id && d.district_name) {
              this.districts.push({ id: d.d_id, name: d.district_name });
              districtIds.push(d.d_id);
            }
          });
        }

        // Получаем ID типов работ
        const workTypeIds = this.workTypes.map((wt) => String(wt.id));

        // Загружаем правила с массивами district_id и work_type_id
        if (districtIds.length > 0 && workTypeIds.length > 0) {
          this.rulesService
            .getRules({
              district_id: districtIds,
              work_type_id: workTypeIds,
            })
            .subscribe({
              next: (rulesResponse) => {
                this.originalRules = rulesResponse.data;
                this.applyFilters();
                this.isLoading = false;
                this.cdr.markForCheck();
              },
              error: (err) => {
                console.error('Error loading rules:', err);
                this.error = 'Ошибка загрузки правил';
                this.isLoading = false;
                this.cdr.markForCheck();
              },
            });
        } else {
          // Нет районов или типов работ - показываем пустой список
          this.originalRules = [];
          this.applyFilters();
          this.isLoading = false;
          this.cdr.markForCheck();
        }
      },
      error: (err) => {
        console.error('Error loading districts or work types:', err);
        this.error = 'Ошибка загрузки данных';
        this.isLoading = false;
        this.cdr.markForCheck();
      },
    });
  }

  onApply() {
    this.applyFilters();
  }

  onReset() {
    this.selectedDistrict = null;
    this.selectedWorkType = null;
    this.selectedRuleType = null;
    this.selectedStatus = null;
    this.applyFilters();
  }

  private applyFilters() {
    let filteredRules = this.originalRules.slice();

    if (this.selectedDistrict) {
      filteredRules = filteredRules.filter((r) => {
        const districtName = this.districtNamesMap[r.district_id];
        return districtName === this.selectedDistrict!.name;
      });
    }

    if (this.selectedWorkType) {
      filteredRules = filteredRules.filter((r) => r.work_type.id === this.selectedWorkType!.id);
    }

    if (this.selectedRuleType) {
      const targetId = this.selectedRuleType.id === 'day' ? 1 : 2;
      filteredRules = filteredRules.filter((r) => r.rule_distribution_id === targetId);
    }

    // Фильтр по статусу
    if (this.selectedStatus) {
      // active -> (сейчас нет поля status в APIRule, но есть логика вычисления статуса)
      // В transformApiRuleToDisplay мы вычисляем статус.
      // Здесь придется продублировать логику или фильтровать уже преобразованные данные.
      // Лучше фильтровать уже преобразованные, но у нас originalRules это ApiRule.
      // Поэтому сначала преобразуем, потом фильтруем статусы
    }

    // Преобразуем в группы
    this.groups = this.transformApiRulesToGroups(filteredRules);

    if (this.selectedStatus) {
      const statusText = this.selectedStatus.id === 'active' ? 'Активно' : 'Неактивно';
      this.groups.forEach((group) => {
        group.rules = group.rules.filter((rule) => rule.status === statusText);
      });
      this.groups = this.groups.filter((g) => g.rules.length > 0);
    }
  }

  private buildDistrictNamesMap(districts: any[]): void {
    this.districtNamesMap = {};

    if (!districts) return;

    districts.forEach((district) => {
      if (district.d_id && district.district_name) {
        this.districtNamesMap[district.d_id] = district.district_name;
      }
    });
  }

  private transformApiRulesToGroups(rules: ApiRule[]): RulesGroup[] {
    // Сначала преобразуем все правила в формат отображения
    const displayRules = rules.map((rule) => this.transformApiRuleToDisplay(rule));

    if (displayRules.length === 0) {
      return [];
    }

    // Группировка
    const groupedMap = new Map<string, WorkRestrictionRuleDisplay[]>();

    displayRules.forEach((rule) => {
      // Ключ группировки: тип работ + дни недели + интервал + тип правила + срок + статус
      const key = `${rule.workType}|${rule.weekDays}|${rule.timeInterval}|${rule.ruleType}|${rule.validity}|${rule.status}`;
      if (!groupedMap.has(key)) {
        groupedMap.set(key, []);
      }
      groupedMap.get(key)!.push(rule);
    });

    const finalRules: WorkRestrictionRuleDisplay[] = [];

    groupedMap.forEach((groupRules) => {
      if (groupRules.length === 1) {
        finalRules.push(groupRules[0]);
      } else {
        // Создаем объединенное правило
        const mergedRule = { ...groupRules[0] };

        // Объединяем районы (уникальные)
        const districts = Array.from(new Set(groupRules.map((r) => r.district))).join(', ');
        // Объединяем типы работ (уникальные)
        const workTypes = Array.from(new Set(groupRules.map((r) => r.workType))).join(', ');

        mergedRule.district = districts;
        mergedRule.workType = workTypes;
        mergedRule._groupOrigins = groupRules.map((r) => r._original);

        finalRules.push(mergedRule);
      }
    });

    const sectorTitle =
      this.filter && this.filter.plotSelected && this.filter.plotSelected.sector_number_str
        ? this.filter.plotSelected.sector_number_str
        : this.filter && this.filter.plotSelected && this.filter.plotSelected.sector_id
          ? this.filter.plotSelected.sector_id.toString()
          : 'Участок';

    return [
      {
        id: 'sector-' + (this.filter.plotSelected.sector_id || 'unknown'),
        title: sectorTitle,
        collapsed: false,
        rules: finalRules,
      },
    ];
  }

  private transformApiRuleToDisplay(rule: ApiRule): WorkRestrictionRuleDisplay {
    const weekDays = this.getWeekDaysString(rule);

    const timeFrom = this.formatTime(rule.time_start);
    const timeTo = this.formatTime(rule.time_end);

    const validity = this.getValidityString(rule.start_date, rule.end_date);

    const status = this.isRuleActive(rule.start_date, rule.end_date) ? 'Активно' : 'Неактивно';

    const districtName = this.districtNamesMap[rule.district_id] || 'Район ' + rule.district_id;

    const ruleType = rule.rule_distribution_type.description === 'В любой день' ? 'По расписанию' : rule.rule_distribution_type.description;

    return {
      id: rule.id,
      district: districtName,
      workType: rule.work_type.description,
      weekDays: weekDays,
      timeInterval: timeFrom + ' — ' + timeTo,
      ruleType: ruleType,
      validity: validity,
      status: status,
      _original: rule,
    };
  }

  private getWeekDaysString(rule: ApiRule): string {
    const days: string[] = [];
    if (rule.monday) days.push('Пн');
    if (rule.tuesday) days.push('Вт');
    if (rule.wednesday) days.push('Ср');
    if (rule.thursday) days.push('Чт');
    if (rule.friday) days.push('Пт');
    if (rule.saturday) days.push('Сб');
    if (rule.sunday) days.push('Вс');
    return days.join(', ') || 'Не указаны';
  }

  private formatTime(time: string): string {
    if (time && time.length >= 5) {
      return time.substring(0, 5);
    }
    return time || '00:00';
  }

  private getValidityString(startDate: string, endDate: string): string {
    const endYear = parseInt(endDate.substring(0, 4), 10);
    if (endYear >= 2035) {
      return 'Бессрочно';
    }

    const formattedStart = this.formatDateForDisplay(startDate);
    const formattedEnd = this.formatDateForDisplay(endDate);

    return formattedStart + ' — ' + formattedEnd;
  }

  private formatDateForDisplay(dateStr: string): string {
    if (dateStr && dateStr.length === 10) {
      const parts = dateStr.split('-');
      if (parts.length === 3) {
        return parts[2] + '.' + parts[1] + '.' + parts[0];
      }
    }
    return dateStr || '';
  }

  private isRuleActive(startDate: string, endDate: string): boolean {
    const now = new Date();
    const start = new Date(startDate);
    const end = new Date(endDate);

    // Сбрасываем время для сравнения только дат
    const currentDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startDateOnly = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    const endDateOnly = new Date(end.getFullYear(), end.getMonth(), end.getDate());

    // Проверяем, попадает ли текущая дата в диапазон start_date <= currentDate <= end_date
    if (currentDate < startDateOnly || currentDate > endDateOnly) {
      return false;
    }

    return true;
  }

  toggleGroup(group: RulesGroup) {
    group.collapsed = !group.collapsed;
  }

  onCreateRule() {
    const sectorTitle =
      this.filter && this.filter.plotSelected && this.filter.plotSelected.sector_number_str
        ? this.filter.plotSelected.sector_number_str
        : this.filter && this.filter.plotSelected && this.filter.plotSelected.sector_id
          ? this.filter.plotSelected.sector_id
          : '';

    const modalRef = this.modalService.open(CreateWorkRestrictionRuleModalComponent, {
      windowClass: 'modal-wrapper modal-wrapper--w-680',
      backdrop: 'static',
    });

    modalRef.componentInstance.sectorTitle = sectorTitle;
    modalRef.componentInstance.isEditMode = false;
    modalRef.componentInstance.workTypes = this.workTypes;
    modalRef.componentInstance.districts = this.districts;

    modalRef.result
      .then((result) => {
        if (result && result.action === 'create') {
          this.executeCreateRules(result);
        }
      })
      .catch(() => {});
  }

  private executeCreateRules(result: any): void {
    const payloadsWithMeta = this.buildCreatePayloadsWithMeta(result);

    if (payloadsWithMeta.length === 0) return;

    this.isSaving = true;
    this.cdr.markForCheck();

    const errors: RuleError[] = [];

    // Создаём массив Observable, каждый с catchError для отслеживания ошибок
    const requests = payloadsWithMeta.map((pm) =>
      this.rulesService.createRule(pm.payload).pipe(
        catchError((err) => {
          const message = this.extractErrorMessage(err);
          errors.push({
            districtName: pm.districtName,
            workTypeName: pm.workTypeName,
            message: message,
          });
          return of(null); // Продолжаем выполнение
        })
      )
    );

    // Выполняем все запросы (каждый с catchError, поэтому forkJoin не упадет)
    forkJoin(requests).subscribe({
      next: () => {
        this.isSaving = false;
        this.cdr.markForCheck();
        this.loadRules();
        if (errors.length > 0) {
          this.showErrorModal(errors, 'Ошибка при создании правил');
        } else {
          this.showSuccessNotification('Правило успешно создано');
        }
      },
      error: (err) => {
        this.isSaving = false;
        this.cdr.markForCheck();
        console.error('Unexpected error:', err);
      },
    });
  }

  private buildCreatePayloadsWithMeta(result: any): { payload: any; districtName: string; workTypeName: string }[] {
    const payloadsWithMeta: { payload: any; districtName: string; workTypeName: string }[] = [];
    const districts = result.selectedDistricts as IdName[];
    const workTypes = result.selectedWorkTypes as IdName[];

    if (!districts || !workTypes || districts.length === 0 || workTypes.length === 0) {
      return [];
    }

    const ruleDistributionId = result.ruleType === 'day' ? 1 : 2;
    const weekdaysArray: number[] = result.weekdays || [];
    const monday = weekdaysArray.includes(1);
    const tuesday = weekdaysArray.includes(2);
    const wednesday = weekdaysArray.includes(3);
    const thursday = weekdaysArray.includes(4);
    const friday = weekdaysArray.includes(5);
    const saturday = weekdaysArray.includes(6);
    const sunday = weekdaysArray.includes(7);

    const timeStart = (result.timeFrom || '00:00') + ':00';
    const timeEnd = (result.timeTo || '23:59') + ':00';

    let startDate: string;
    let endDate: string;

    if (result.validityType === 'forever') {
      startDate = this.formatDateToApi(new Date());
      endDate = '2099-12-31';
    } else {
      startDate = result.periodFrom && result.periodFrom.dateMillis ? this.formatDateToApi(new Date(result.periodFrom.dateMillis)) : this.formatDateToApi(new Date());
      endDate = result.periodTo && result.periodTo.dateMillis ? this.formatDateToApi(new Date(result.periodTo.dateMillis)) : '2099-12-31';
    }

    const basePayload = {
      rule_distribution_id: ruleDistributionId,
      monday,
      tuesday,
      wednesday,
      thursday,
      friday,
      saturday,
      sunday,
      time_start: timeStart,
      time_end: timeEnd,
      start_date: startDate,
      end_date: endDate,
    };

    districts.forEach((d) => {
      workTypes.forEach((w) => {
        payloadsWithMeta.push({
          payload: {
            ...basePayload,
            district_id: typeof d.id === 'string' ? parseInt(d.id as string, 10) : d.id,
            work_type_id: typeof w.id === 'string' ? parseInt(w.id as string, 10) : w.id,
          },
          districtName: d.name,
          workTypeName: w.name,
        });
      });
    });

    return payloadsWithMeta;
  }

  private showErrorModal(errors: RuleError[], title: string): void {
    const modalRef = this.modalService.open(RuleErrorModalComponent, {
      windowClass: 'modal-wrapper modal-wrapper--w-680',
      backdrop: 'static',
    });
    modalRef.componentInstance.errors = errors;
    modalRef.componentInstance.title = title;
  }

  private buildCreatePayloads(result: any): any[] {
    const payloads: any[] = [];
    const districts = result.selectedDistricts as IdName[];
    const workTypes = result.selectedWorkTypes as IdName[];

    if (!districts || !workTypes || districts.length === 0 || workTypes.length === 0) {
      return [];
    }

    const ruleDistributionId = result.ruleType === 'day' ? 1 : 2;
    const weekdaysArray: number[] = result.weekdays || [];
    const monday = weekdaysArray.includes(1);
    const tuesday = weekdaysArray.includes(2);
    const wednesday = weekdaysArray.includes(3);
    const thursday = weekdaysArray.includes(4);
    const friday = weekdaysArray.includes(5);
    const saturday = weekdaysArray.includes(6);
    const sunday = weekdaysArray.includes(7);

    const timeStart = (result.timeFrom || '00:00') + ':00';
    const timeEnd = (result.timeTo || '23:59') + ':00';

    let startDate: string;
    let endDate: string;

    if (result.validityType === 'forever') {
      startDate = this.formatDateToApi(new Date());
      endDate = '2099-12-31';
    } else {
      startDate = result.periodFrom && result.periodFrom.dateMillis ? this.formatDateToApi(new Date(result.periodFrom.dateMillis)) : this.formatDateToApi(new Date());
      endDate = result.periodTo && result.periodTo.dateMillis ? this.formatDateToApi(new Date(result.periodTo.dateMillis)) : '2099-12-31';
    }

    const basePayload = {
      rule_distribution_id: ruleDistributionId,
      monday: monday,
      tuesday: tuesday,
      wednesday: wednesday,
      thursday: thursday,
      friday: friday,
      saturday: saturday,
      sunday: sunday,
      time_start: timeStart,
      time_end: timeEnd,
      start_date: startDate,
      end_date: endDate,
    };

    districts.forEach((d) => {
      workTypes.forEach((w) => {
        payloads.push({
          ...basePayload,
          district_id: typeof d.id === 'string' ? parseInt(d.id as string, 10) : d.id,
          work_type_id: typeof w.id === 'string' ? parseInt(w.id as string, 10) : w.id,
        });
      });
    });

    return payloads;
  }

  private formatDateToApi(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return year + '-' + month + '-' + day;
  }

  onEditRule(rule: WorkRestrictionRuleDisplay, group: RulesGroup) {
    const modalRef = this.modalService.open(CreateWorkRestrictionRuleModalComponent, {
      windowClass: 'modal-wrapper modal-wrapper--w-680',
      backdrop: 'static',
    });

    let districtIds: number[] = [];
    let workTypeIds: number[] = [];

    const getSafeId = (id: any) => (typeof id === 'string' ? parseInt(id, 10) : id);

    if (rule._groupOrigins && rule._groupOrigins.length > 0) {
      districtIds = Array.from(new Set(rule._groupOrigins.map((r) => getSafeId(r.district_id))));
      workTypeIds = Array.from(new Set(rule._groupOrigins.map((r) => getSafeId(r.work_type.id))));
    } else {
      districtIds = [getSafeId(rule._original.district_id)];
      workTypeIds = [getSafeId(rule._original.work_type.id)];
    }

    modalRef.componentInstance.sectorTitle = group.title;
    modalRef.componentInstance.isEditMode = true;
    modalRef.componentInstance.workTypes = this.workTypes;
    modalRef.componentInstance.districts = this.districts;
    modalRef.componentInstance.initialDistrictIds = districtIds;
    modalRef.componentInstance.initialWorkTypeIds = workTypeIds;

    modalRef.componentInstance.editRule = {
      id: rule.id.toString(),
      district: rule.district,
      workType: rule.workType,
      weekDays: rule.weekDays,
      timeInterval: rule.timeInterval,
      ruleType: rule.ruleType,
      validity: rule.validity,
      status: rule.status,
    };

    modalRef.result
      .then((result) => {
        if (!result) return;

        if (result.action === 'delete') {
          const oldIds = rule._groupOrigins ? rule._groupOrigins.map((r) => r.id) : [rule.id];
          const deleteObs = oldIds.map((id) => this.rulesService.deleteRule(id));

          this.isSaving = true;
          this.cdr.markForCheck();

          forkJoin(deleteObs).subscribe({
            next: () => {
              this.isSaving = false;
              this.cdr.markForCheck();
              this.loadRules();
              this.showSuccessNotification('Правило успешно удалено');
            },
            error: (err) => {
              this.isSaving = false;
              this.cdr.markForCheck();
              console.error('Error deleting rules:', err);
              const message = this.extractErrorMessage(err);
              this.showErrorModal([{ districtName: rule.district, workTypeName: rule.workType, message }], 'Ошибка при удалении правила');
            },
          });
        } else if (result.action === 'update') {
          const payloadsWithMeta = this.buildCreatePayloadsWithMeta(result);
          if (payloadsWithMeta.length === 0) return;

          const oldIds = rule._groupOrigins ? rule._groupOrigins.map((r) => r.id) : [rule.id];
          const isSingleToSingle = oldIds.length === 1 && payloadsWithMeta.length === 1;

          this.isSaving = true;
          this.cdr.markForCheck();

          if (isSingleToSingle) {
            // Используем PATCH для обновления одиночного правила
            const ruleId = oldIds[0];
            const pm = payloadsWithMeta[0];

            this.rulesService.updateRule(ruleId, pm.payload).subscribe({
              next: () => {
                this.isSaving = false;
                this.cdr.markForCheck();
                this.loadRules();
                this.showSuccessNotification('Правило успешно обновлено');
              },
              error: (err) => {
                this.isSaving = false;
                this.cdr.markForCheck();
                console.error('Error updating rule:', err);
                const message = this.extractErrorMessage(err);
                this.showErrorModal([{ districtName: pm.districtName, workTypeName: pm.workTypeName, message }], 'Ошибка при обновлении правила');
              },
            });
          } else {
            // Используем delete-then-create для множественных изменений
            const deleteObs = oldIds.map((id) => this.rulesService.deleteRule(id));

            forkJoin(deleteObs)
              .pipe(
                switchMap(() => {
                  const errors: RuleError[] = [];
                  const createRequests = payloadsWithMeta.map((pm) =>
                    this.rulesService.createRule(pm.payload).pipe(
                      catchError((err) => {
                        const message = this.extractErrorMessage(err);
                        errors.push({
                          districtName: pm.districtName,
                          workTypeName: pm.workTypeName,
                          message: message,
                        });
                        return of(null);
                      })
                    )
                  );
                  return forkJoin(createRequests).pipe(
                    switchMap((results) => {
                      if (errors.length > 0) {
                        this.showErrorModal(errors, 'Ошибка при обновлении правил');
                      } else {
                        this.showSuccessNotification('Правило успешно обновлено');
                      }
                      return of(results);
                    })
                  );
                })
              )
              .subscribe({
                next: () => {
                  this.isSaving = false;
                  this.cdr.markForCheck();
                  this.loadRules();
                },
                error: (err) => {
                  this.isSaving = false;
                  this.cdr.markForCheck();
                  console.error('Error updating rules:', err);
                  const message = this.extractErrorMessage(err);
                  this.showErrorModal([{ districtName: rule.district, workTypeName: rule.workType, message }], 'Ошибка при обновлении правила');
                  this.loadRules();
                },
              });
          }
        }
      })
      .catch(() => {});
  }

  private extractErrorMessage(err: any): string {
    if (err.error.metadata.message) {
      const message = err.error.metadata.message;
      if (typeof message === 'string') {
        return message;
      }
      if (Array.isArray(message) && message.length > 0) {
        return message.map((m: any) => m.msg || JSON.stringify(m)).join('; ');
      }
    }
    return 'Произошла неизвестная ошибка';
  }

  private showSuccessNotification(message: string): void {
    this.successMessage = message;
    this.cdr.markForCheck();

    setTimeout(() => {
      this.successMessage = null;
      this.cdr.markForCheck();
    }, 3000);
  }
}
